import type { PreOrderData } from '@/lib/checkout/preOrder';
import { parsePreorderPayload } from '@/lib/checkout/parsePreorderPayload';
import type { CheckoutPreOrderSummary } from '@/lib/checkout/preOrderSummary';
import type { TransactionType } from '@/hooks/usePaymentProcessor';
import { getCountryCodeFromChannel } from '@/lib/const';
import { isTaxIncluded } from '@/lib/tax';

export type PreorderCapableOrder = {
  orderType?: string | null;
  preOrderData?: string | null;
  total?: {
    gross: { amount: number; currency: string };
    net: { amount: number; currency: string };
  };
  totalAuthorized?: { amount: number; currency: string };
  totalCharged?: { amount: number; currency: string };
  totalBalance?: { amount: number; currency: string };
  channel?: { slug: string } | null;
};

export function extractPreorderDataFromOrder(
  order: PreorderCapableOrder | null | undefined
): PreOrderData | null {
  if (order?.orderType !== 'PRE_ORDER' || !order?.preOrderData) {
    return null;
  }

  const parsed = parsePreorderPayload<PreOrderData>(order.preOrderData);
  if (!parsed?.deposit || parsed.deposit <= 0) {
    return null;
  }

  return parsed;
}

export function isOrderDepositPaid(order: PreorderCapableOrder | null | undefined): boolean {
  const preOrderData = extractPreorderDataFromOrder(order);

  if (!preOrderData || !order) {
    return false;
  }

  const deposit = preOrderData.deposit;
  const totalAuthorized = order.totalAuthorized?.amount ?? 0;
  const totalCharged = order.totalCharged?.amount ?? 0;
  const totalGross = order.total?.gross?.amount ?? 0;
  const totalBalanceAmount = order.totalBalance?.amount ?? 0;

  const isDepositAuthorized = totalAuthorized > 0 && deposit >= totalAuthorized;
  const isDepositCharged = totalCharged > 0 && deposit >= totalCharged;
  const isAbsBalanceLessThanOrder = Math.abs(totalBalanceAmount) < totalGross;

  return isDepositCharged || isDepositAuthorized || isAbsBalanceLessThanOrder;
}

export function extractOrderBalance(order: PreorderCapableOrder | null | undefined): {
  amount: number;
  currency: string;
} {
  return {
    amount: Math.abs(order?.totalBalance?.amount ?? 0),
    currency: order?.totalBalance?.currency ?? order?.total?.gross?.currency ?? 'USD',
  };
}

export function isOrderFullyCovered(order: PreorderCapableOrder | null | undefined): boolean {
  const balance = extractOrderBalance(order);
  const totalAuthorized = order?.totalAuthorized?.amount ?? 0;

  const isBalanceCovered = balance.amount === 0;
  const isAuthorizationCoveringBalance = balance.amount > 0 && totalAuthorized >= balance.amount;

  return isBalanceCovered || isAuthorizationCoveringBalance;
}

export function resolveOrderShowTax(channelSlug?: string | null): boolean {
  const channelCountry = getCountryCodeFromChannel(channelSlug ?? undefined);
  return channelCountry ? isTaxIncluded(channelCountry) : true;
}

/**
 * Full watch total from deposit % — order.total after deposit is only the charged slice.
 *
 * Example Input:
 * deposit=1500, deposit_percentage=30
 *
 * Example Output:
 * 5000
 */
export function derivePreOrderFullTotal(
  preOrderData: PreOrderData,
  showTax: boolean
): number | undefined {
  const pct = preOrderData.deposit_percentage;
  if (!pct || pct <= 0) {
    return undefined;
  }

  const deposit = showTax ? preOrderData.deposit : preOrderData.deposit_net;
  return +(deposit / (pct / 100)).toFixed(2);
}

/** Gross when channel includes tax, or when Saleor has already calculated sales tax (gross ≠ net). */
function shouldUseGrossPreOrderSummary(order: PreorderCapableOrder): boolean {
  const showTax = resolveOrderShowTax(order.channel?.slug);
  const hasCalculatedTax = order.total?.gross.amount !== order.total?.net.amount;
  return showTax || hasCalculatedTax;
}

export function getOrderPreOrderSummary(
  order: PreorderCapableOrder,
  preOrderData: PreOrderData,
  showTax: boolean,
  fullOrderTotal?: number
): CheckoutPreOrderSummary {
  const deposit = showTax ? preOrderData.deposit : preOrderData.deposit_net;
  const total = fullOrderTotal ?? (showTax ? order.total!.gross.amount : order.total!.net.amount);
  const currency = showTax ? order.total!.gross.currency : order.total!.net.currency;

  return {
    total,
    deposit,
    balance: total - deposit,
    depositPercentage: preOrderData.deposit_percentage,
    currency,
  };
}

/**
 * Pre-order summary for GTM — full watch total from deposit % when order.total is deposit-only.
 */
export function getOrderPreOrderSummaryForAnalytics(
  order: PreorderCapableOrder,
  preOrderData: PreOrderData,
  showTax: boolean
): CheckoutPreOrderSummary {
  return getOrderPreOrderSummary(
    order,
    preOrderData,
    showTax,
    derivePreOrderFullTotal(preOrderData, showTax)
  );
}

export type PayPerLinkCharge = {
  amount: number;
  transactionType: TransactionType;
  preOrderSummary?: CheckoutPreOrderSummary;
  isPreOrder: boolean;
  isDepositPaid: boolean;
};

/**
 * Prepaid deposit/balance for confirmation and account order detail.
 * Attaches only when deposit is paid, balance remains, and the order is not fully covered.
 * Amounts are tax-inclusive (metafield deposit + total.gross − deposit).
 *
 * Example Input:
 * order with PRE_ORDER type, deposit paid, balance remaining
 *
 * Example Output:
 * CheckoutPreOrderSummary with gross deposit/balance
 */
export function attachPreOrderSummary(
  order: PreorderCapableOrder
): CheckoutPreOrderSummary | undefined {
  const preOrderData = extractPreorderDataFromOrder(order);
  if (!preOrderData || !isOrderDepositPaid(order) || isOrderFullyCovered(order)) {
    return undefined;
  }

  const summary = getOrderPreOrderSummary(order, preOrderData, true);

  if (summary.balance <= 0) {
    return undefined;
  }

  return summary;
}

/**
 * Pre-order summary for GTM purchase fallback — does not require deposit-paid gate.
 *
 * Example Input:
 * PRE_ORDER with valid preOrderData right after deposit capture
 *
 * Example Output:
 * CheckoutPreOrderSummary (deposit/balance/total)
 */
export function attachPreOrderSummaryForAnalytics(
  order: PreorderCapableOrder
): CheckoutPreOrderSummary | undefined {
  const preOrderData = extractPreorderDataFromOrder(order);
  if (!preOrderData || !order.total) {
    return undefined;
  }

  const showTax = shouldUseGrossPreOrderSummary(order);
  return getOrderPreOrderSummaryForAnalytics(order, preOrderData, showTax);
}

/** Paid/display total for confirmation — always gross (sales tax included for US/CA). */
export function resolveOrderDisplayTotal(order: PreorderCapableOrder): number {
  return order.total?.gross?.amount ?? 0;
}

export function resolvePayPerLinkCharge(order: PreorderCapableOrder): PayPerLinkCharge {
  const preOrderData = extractPreorderDataFromOrder(order);
  if (!preOrderData) {
    return {
      amount: order.total!.gross.amount,
      transactionType: 'FULL',
      isPreOrder: false,
      isDepositPaid: false,
    };
  }

  const isDepositPaid = isOrderDepositPaid(order);
  const useGrossSummary = shouldUseGrossPreOrderSummary(order);
  const summary = getOrderPreOrderSummary(order, preOrderData, useGrossSummary);

  if (isDepositPaid) {
    return {
      amount: summary.balance,
      transactionType: 'BALANCE',
      preOrderSummary: shouldShowPrepaidConfirmationSummary(order) ? summary : undefined,
      isPreOrder: true,
      isDepositPaid: true,
    };
  }

  return {
    amount: summary.deposit,
    transactionType: 'DEPOSIT',
    preOrderSummary: summary,
    isPreOrder: true,
    isDepositPaid: false,
  };
}

export function shouldShowPrepaidConfirmationSummary(
  order: PreorderCapableOrder | null | undefined
): boolean {
  const preOrderData = extractPreorderDataFromOrder(order);
  if (!preOrderData || !order) {
    return false;
  }

  if (!isOrderDepositPaid(order)) {
    return false;
  }

  const summary = getOrderPreOrderSummary(
    order,
    preOrderData,
    shouldUseGrossPreOrderSummary(order)
  );

  if (summary.balance <= 0) {
    return false;
  }

  if (isOrderFullyCovered(order)) {
    return false;
  }

  return true;
}
